import React, { useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";
import KLButton from "../../../components/KLButton";
import KLBusinessOnboardingProgress from "../../../components/KLBusinessOnboardingProgress";
import {
  ChipServiceDescription,
  chipSx,
  ChipWrapper,
  Container,
  InfoText,
  Subtitle,
  Title,
} from "../../styles";
import { useStep } from "../../../context/StepContext";
import Chip from "@mui/material/Chip";
import { isEqual } from "lodash";
import { AvailabilityType } from "../../../constants/types";
import {
  BusinessCategory,
  CategoryConfig,
  categoryConfigMap,
} from "../../../constants/customers/categoryConfigMap";
import { FlowType, BusinessRouteStep } from "../../../constants/routes";
import CryptoJS from "crypto-js";
import { StorageKey, StorageKeys } from "../../../constants/localStorage";
import useLocalStorage from "../../../hooks/useLocalStorage";

const availabilityOptions = [
  {
    value: AvailabilityType.Weekly,
    label: "אני רוצה לתכנן זמינות כל שבוע",
    elaborate: "נציע לכם תאריכים ושעות של זמינות כל שבוע",
  },
  {
    value: AvailabilityType.BiWeekly,
    label: "אני רוצה לתכנן זמינות כל שבועיים",
    elaborate: "נציע לכם תאריכים ושעות של זמינות כל שבועיים",
  },
  {
    value: AvailabilityType.WeeklyRecurring,
    label: "אני רוצה לתכנן זמינות קבועה",
    elaborate: "נציע לכם תאריכים ושעות לזמינות קבועה",
  },
];

const AuthorizeScreen: React.FC = () => {
  const { markStepAsCompleted, onRedirect } = useStep();
  const { businessName, ownerName, phone, email, category, services } =
    useSelector((state: RootState) => state.businesses);

  const { storedValue: verifiedOtpToken } = useLocalStorage<StorageKey | null>(
    StorageKeys.VerifiedBusinessOtpToken,
    null,
  );

  const [selectedAvailability, setSelectedAvailability] = useState<
    string | null
  >(null);

  const categoryConfig: CategoryConfig | undefined = category
    ? categoryConfigMap[category as BusinessCategory]
    : undefined;

  const term = categoryConfig?.term || "תור";
  const pluralTerm = categoryConfig?.pluralTerm || `${term}ים`;

  const handleAuthorize = () => {
    const formData = {
      ownerName,
      businessName,
      phone,
      email,
      category,
      services,
      availability: selectedAvailability,
    };

    let encryptedToken = "";

    if (verifiedOtpToken) {
      try {
        const encryptionKey = process.env.REACT_APP_STATE_ENCRYPTION;
        encryptedToken = CryptoJS.AES.encrypt(
          verifiedOtpToken,
          encryptionKey!,
        ).toString();
      } catch (error) {
        console.error("Error encrypting token:", error);
      }
    }

    const statePayload = {
      formData,
      token: encryptedToken,
    };

    const state = encodeURIComponent(JSON.stringify(statePayload));

    markStepAsCompleted(FlowType.Business, BusinessRouteStep.Authorize);
    onRedirect(true);

    window.location.href = `${process.env.REACT_APP_BACKEND_API}/api/auth/authorize-google-calendar?state=${state}`;
  };

  return (
    <Container>
      <KLBusinessOnboardingProgress currentStep={3} />
      <Title>אישור גישה ליומן Google</Title>
      <Subtitle>למה צריך את ההרשאה?</Subtitle>
      <InfoText>
        נדרש אישור גישה ליומן Google כדי לנהל את ה{pluralTerm} שלך אוטומטית דרך
        המערכת שלנו. זה יאפשר לנו להוסיף ולשנות {pluralTerm} עבורך.
      </InfoText>

      <Title>בחרו את אופן הזמינות שלכם</Title>

      {availabilityOptions.map((availability) => (
        <ChipWrapper key={availability.value}>
          <Chip
            sx={chipSx(isEqual(selectedAvailability, availability.value))}
            label={
              <>
                <ChipServiceDescription>
                  {availability.label}
                </ChipServiceDescription>
                <span>{availability.elaborate}</span>
              </>
            }
            color={
              selectedAvailability === availability.value
                ? "primary"
                : "default"
            }
            onClick={() => setSelectedAvailability(availability.value)}
            style={{ padding: "1.25rem", width: "100%" }}
          />
        </ChipWrapper>
      ))}

      <KLButton
        onClick={handleAuthorize}
        title="אשר גישה"
        disabled={!selectedAvailability}
      />
    </Container>
  );
};

export default AuthorizeScreen;
